import React, { useState } from 'react';
import { ArrowDownLeft, Loader2 } from 'lucide-react';
import { submitClientRefund } from '../../services/walletApi';
import {
  TreasuryUltraAlert,
  TreasuryUltraBtn,
  TreasuryUltraField,
  TreasuryUltraInput,
  TreasuryUltraSelect,
  TreasuryUltraShell,
} from './TreasuryUltraShell';

export default function ClientRefundForm({ bookings = [], onRefunded }) {
  const [bookingRef, setBookingRef] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const refundable = (bookings || []).filter((b) => {
    const status = String(b.status || '').toLowerCase();
    return status === 'active' && Number(b.advance_paid || 0) > 0;
  });

  const selected = refundable.find((b) => b.booking_ref === bookingRef);
  const scrollable = refundable.length > 4;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMessage('');
    setError('');
    try {
      const result = await submitClientRefund({
        bookingRef: bookingRef.trim(),
        note: note.trim() || undefined,
      });
      if (result.pendingSecondApproval) {
        setMessage(`Refund queued (≥ ₹${Number(result.thresholdInr || 10000).toLocaleString('en-IN')}). A different admin must approve in Maker–Checker.`);
        setBookingRef('');
        setNote('');
      } else if (result.duplicate) {
        setMessage('Refund was already processed for this booking.');
      } else {
        setMessage(
          `Refunded ₹${Number(result.amount || 0).toLocaleString('en-IN')} to client wallet. Booking ${result.bookingRef} cancelled.`,
        );
        setBookingRef('');
        setNote('');
        if (onRefunded) onRefunded();
      }
    } catch (err) {
      setError(err.message || 'Refund failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <TreasuryUltraShell
      title="Refund to Client Wallet"
      subtitle="Treasury escrow → client wallet (cancels Active booking)"
      icon={String.fromCodePoint(0x21A9)}
      accent="refund"
      badge={refundable.length ? `${refundable.length} eligible` : '0'}
      scrollable={scrollable}
      scrollMaxHeightClass="max-h-[min(380px,48vh)]"
      showScrollHint={scrollable}
      scrollHint="Scroll refundable bookings"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <TreasuryUltraAlert tone="info">
          Only Active bookings with advance paid. Completed or settled bookings are blocked automatically.
        </TreasuryUltraAlert>

        <div className="grid gap-3 sm:grid-cols-2">
          <TreasuryUltraField label="Booking reference">
            <TreasuryUltraSelect
              value={bookingRef}
              onChange={(e) => setBookingRef(e.target.value)}
              required
            >
              <option value="">Select booking…</option>
              {refundable.map((b) => (
                <option key={b.id || b.booking_ref} value={b.booking_ref}>
                  {b.booking_ref} — ₹{Number(b.advance_paid).toLocaleString('en-IN')}
                  {b.client_name ? ` · ${b.client_name}` : ''}
                </option>
              ))}
            </TreasuryUltraSelect>
          </TreasuryUltraField>

          <TreasuryUltraField label="Admin note" hint="Optional cancellation reason">
            <TreasuryUltraInput
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Cancellation reason"
              maxLength={200}
            />
          </TreasuryUltraField>
        </div>

        {selected ? (
          <div className="de-treasury-ultra-preview">
            Will refund{' '}
            <strong className="text-de-gold">₹{Number(selected.advance_paid).toLocaleString('en-IN')}</strong>
            {' '}from DE treasury → client wallet
            {selected.client_name ? ` (${selected.client_name})` : ''}.
          </div>
        ) : null}

        {refundable.length === 0 ? (
          <TreasuryUltraAlert tone="warn">No Active bookings with advance available for refund.</TreasuryUltraAlert>
        ) : null}

        {error ? <TreasuryUltraAlert tone="error">{error}</TreasuryUltraAlert> : null}
        {message ? <TreasuryUltraAlert tone="success">{message}</TreasuryUltraAlert> : null}

        <TreasuryUltraBtn
          variant="refund"
          type="submit"
          disabled={busy || !bookingRef}
          className="w-full sm:w-auto"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowDownLeft className="h-4 w-4" />}
          {busy ? 'Processing…' : 'Refund to client wallet'}
        </TreasuryUltraBtn>
      </form>
    </TreasuryUltraShell>
  );
}
